/**
 * Velox Legal Notice content (Velox Sàrl, Luxembourg).
 * Source: legal text provided by Velox — last updated 31.08.2026.
 * Kept out of the i18n locale files because of its length.
 */

export interface LegalSection {
  title: string;
  body: string;
}

export interface LegalNoticeContent {
  title: string;
  subtitle: string;
  lastUpdated: string;
  back: string;
  sections: LegalSection[];
}

const IDENTITY_EN = `This website, available at www.myvelox.eu, is operated by:

Velox Sàrl, a private limited liability company (société à responsabilité limitée) existing under the laws of the Grand Duchy of Luxembourg.

Registered office: 38, rue Wilson, L-2732 Luxembourg, Grand Duchy of Luxembourg.
Luxembourg Trade and Companies Register (RCS Luxembourg) number: B306257.
Share capital: EUR 12,000 (fixed).
Business authorisation (autorisation d'établissement) N° 10187403/0, issued under the amended Law of 2 September 2011 regulating access to the professions of craftsman, trader, industrialist, and certain liberal professions.
VAT identification number: LU37407003.
Email: info@myvelox.eu.`;

const en: LegalNoticeContent = {
  title: 'Legal Notice',
  subtitle: 'Velox Sàrl — www.myvelox.eu',
  lastUpdated: 'Last updated: 31.08.2026',
  back: 'Back to home',
  sections: [
    { title: '1. Identification of the website operator', body: IDENTITY_EN },
    {
      title: '2. General terms and conditions of use',
      body: `The user of this website (the "user") acknowledges that they have verified that the computer configuration used does not contain any viruses or other malicious software and that it operates correctly.

The user is solely responsible for the selection, use, and interpretation of the data which they consult, search, and transmit on the internet.

Velox Sàrl reserves the right to modify this Legal Notice at any time. The user shall abide by the version of this Legal Notice available on the website at the time of use.`,
    },
    {
      title: '3. Contents of the website',
      body: `Velox Sàrl shall not be held responsible for the presence of viruses or malicious software on its website.

Velox Sàrl reserves the right to correct the content of this website at any time and without notice.

Velox Sàrl undertakes reasonable efforts to offer available and verified information and resources to users, but cannot be held responsible for errors, unavailability, or the invalid character of the information.

Under no circumstances can the information contained on this website constitute a warranty. For further information, please contact Velox Sàrl directly.

Any user who discovers inaccuracies, erroneous information, or information of a clearly unlawful nature is invited to inform Velox Sàrl at the address set out in Section 1.`,
    },
    {
      title: '4. Use of hyperlinks',
      body: `This website may include information made available by hyperlinks to other sites which have not been developed by Velox Sàrl.

Velox Sàrl shall under no circumstances be liable for hyperlinks to other sites from this website.

The existence of a link from this website to another site shall not constitute any acquiescence or warranty on the part of Velox Sàrl with respect to the content of, or any person operating, that other site.

The creation of hyperlinks to this website is subject to the explicit prior approval of Velox Sàrl.`,
    },
    {
      title: '5. Intellectual property',
      body: `All information available on this website is protected by copyright. Unless otherwise indicated, reproduction of text from this site for non-commercial purposes, including in educational contexts, is permitted, subject to the following conditions:

(a) free dissemination;
(b) respect for the integrity of the reproduced content, with no modification or alteration of any kind; and
(c) explicit mention of www.myvelox.eu as the source, together with a statement that reproduction rights are reserved and limited.

Any exception to these conditions is subject to the prior written approval of Velox Sàrl and, where applicable, the author of the content.

Unless otherwise stated, photographs on this website are protected by copyright and are the property of Velox Sàrl.`,
    },
    {
      title: '6. Data privacy',
      body: `For information on how this website and the Carrier Portal process personal data, including the use of cookies, please refer to Velox's Privacy Notice and Cookie Policy, both available via a dedicated link on the website.`,
    },
    {
      title: '7. Responsibility',
      body: `The user accepts that any access to, or use of, this website is at their own risk.

Velox Sàrl declines all liability for direct or indirect damage which may be caused by use of the website, in particular in relation to an interruption or cessation of the website's operation, or a malfunction resulting from maintenance or upgrades to Velox Sàrl's computer systems, technical faults, network overloads, faults in telephone lines, errors or negligence by internet service providers or third parties, or a virus acquired via the internet.`,
    },
    {
      title: '8. Governing law and jurisdiction',
      body: `This Legal Notice is governed by the laws of the Grand Duchy of Luxembourg.

The user is informed that only the courts of Luxembourg, Grand Duchy of Luxembourg, have jurisdiction to rule on any dispute relating to the use, interpretation, or implementation of the information and data featured on this website.`,
    },
    {
      title: '9. Contact',
      body: `For any questions relating to this Legal Notice, please contact Velox Sàrl using the details set out in Section 1.`,
    },
  ],
};

const fr: LegalNoticeContent = {
  title: 'Mentions légales',
  subtitle: 'Velox Sàrl — www.myvelox.eu',
  lastUpdated: 'Dernière mise à jour : 31.08.2026',
  back: "Retour à l'accueil",
  sections: [
    {
      title: "1. Identification de l'éditeur du site",
      body: `Ce site internet, accessible à l'adresse www.myvelox.eu, est exploité par :

Velox Sàrl, société à responsabilité limitée de droit luxembourgeois.

Siège social : 38, rue Wilson, L-2732 Luxembourg, Grand-Duché de Luxembourg.
Registre de Commerce et des Sociétés, Luxembourg : B306257.
Capital social : 12 000 EUR (fixe).
Autorisation d'établissement N° 10187403/0, délivrée en vertu de la loi modifiée du 2 septembre 2011 réglementant l'accès aux professions d'artisan, de commerçant, d'industriel ainsi qu'à certaines professions libérales.
Numéro d'identification TVA : LU37407003.
Courriel : info@myvelox.eu.`,
    },
    {
      title: "2. Conditions générales d'utilisation",
      body: `L'utilisateur de ce site (l'« utilisateur ») reconnaît avoir vérifié que la configuration informatique utilisée ne contient aucun virus ni autre logiciel malveillant et qu'elle fonctionne correctement.

L'utilisateur est seul responsable du choix, de l'utilisation et de l'interprétation des données qu'il consulte, recherche et transmet sur internet.

Velox Sàrl se réserve le droit de modifier les présentes mentions légales à tout moment. L'utilisateur est tenu de respecter la version disponible sur le site au moment de son utilisation.`,
    },
    {
      title: '3. Contenu du site',
      body: `Velox Sàrl ne peut être tenue responsable de la présence de virus ou de logiciels malveillants sur son site.

Velox Sàrl se réserve le droit de corriger le contenu de ce site à tout moment et sans préavis.

Velox Sàrl met en œuvre des efforts raisonnables pour offrir aux utilisateurs des informations et ressources disponibles et vérifiées, mais ne peut être tenue responsable des erreurs, de l'indisponibilité ou du caractère non valable des informations.

En aucun cas les informations contenues sur ce site ne constituent une garantie. Pour toute information complémentaire, veuillez contacter directement Velox Sàrl.

Tout utilisateur qui constaterait des inexactitudes, des informations erronées ou manifestement illicites est invité à en informer Velox Sàrl à l'adresse indiquée à la section 1.`,
    },
    {
      title: '4. Utilisation des hyperliens',
      body: `Ce site peut contenir des informations mises à disposition par des hyperliens vers d'autres sites qui n'ont pas été développés par Velox Sàrl.

Velox Sàrl ne saurait en aucun cas être tenue responsable des hyperliens vers d'autres sites depuis le présent site.

L'existence d'un lien depuis ce site vers un autre site ne constitue ni une approbation ni une garantie de la part de Velox Sàrl quant au contenu de cet autre site ou à la personne qui l'exploite.

La création d'hyperliens vers ce site est soumise à l'accord préalable et explicite de Velox Sàrl.`,
    },
    {
      title: '5. Propriété intellectuelle',
      body: `Toutes les informations disponibles sur ce site sont protégées par le droit d'auteur. Sauf indication contraire, la reproduction des textes de ce site à des fins non commerciales, y compris pédagogiques, est autorisée aux conditions suivantes :

(a) diffusion gratuite ;
(b) respect de l'intégrité du contenu reproduit, sans modification ni altération d'aucune sorte ; et
(c) mention explicite de www.myvelox.eu comme source, accompagnée de l'indication que les droits de reproduction sont réservés et limités.

Toute exception à ces conditions est soumise à l'accord écrit préalable de Velox Sàrl et, le cas échéant, de l'auteur du contenu.

Sauf mention contraire, les photographies présentes sur ce site sont protégées par le droit d'auteur et sont la propriété de Velox Sàrl.`,
    },
    {
      title: '6. Protection des données',
      body: `Pour toute information sur la manière dont ce site et le portail transporteur traitent les données à caractère personnel, y compris l'usage des cookies, veuillez consulter la politique de confidentialité et la politique en matière de cookies de Velox, toutes deux accessibles via un lien dédié sur le site.`,
    },
    {
      title: '7. Responsabilité',
      body: `L'utilisateur accepte que tout accès ou toute utilisation de ce site se fasse à ses propres risques.

Velox Sàrl décline toute responsabilité pour les dommages directs ou indirects pouvant résulter de l'utilisation du site, notamment en cas d'interruption ou de cessation de son fonctionnement, de dysfonctionnement lié à la maintenance ou à la mise à niveau des systèmes informatiques de Velox Sàrl, de défaillances techniques, de surcharges de réseau, de défauts des lignes téléphoniques, d'erreurs ou de négligences de fournisseurs d'accès à internet ou de tiers, ou d'un virus contracté via internet.`,
    },
    {
      title: '8. Droit applicable et juridiction',
      body: `Les présentes mentions légales sont régies par le droit du Grand-Duché de Luxembourg.

L'utilisateur est informé que seuls les tribunaux de Luxembourg, Grand-Duché de Luxembourg, sont compétents pour statuer sur tout litige relatif à l'utilisation, à l'interprétation ou à la mise en œuvre des informations et données figurant sur ce site.`,
    },
    {
      title: '9. Contact',
      body: `Pour toute question relative aux présentes mentions légales, veuillez contacter Velox Sàrl aux coordonnées indiquées à la section 1.`,
    },
  ],
};

const es: LegalNoticeContent = {
  title: 'Aviso legal',
  subtitle: 'Velox Sàrl — www.myvelox.eu',
  lastUpdated: 'Última actualización: 31.08.2026',
  back: 'Volver al inicio',
  sections: [
    {
      title: '1. Identificación del operador del sitio web',
      body: `Este sitio web, disponible en www.myvelox.eu, es operado por:

Velox Sàrl, sociedad de responsabilidad limitada (société à responsabilité limitée) constituida conforme a la legislación del Gran Ducado de Luxemburgo.

Domicilio social: 38, rue Wilson, L-2732 Luxemburgo, Gran Ducado de Luxemburgo.
Registro Mercantil de Luxemburgo (RCS Luxembourg): B306257.
Capital social: 12.000 EUR (fijo).
Autorización de establecimiento N° 10187403/0, expedida conforme a la Ley modificada de 2 de septiembre de 2011.
Número de identificación a efectos de IVA: LU37407003.
Correo electrónico: info@myvelox.eu.`,
    },
    {
      title: '2. Condiciones generales de uso',
      body: `El usuario de este sitio web (el «usuario») reconoce haber verificado que la configuración informática utilizada no contiene virus ni otro software malicioso y que funciona correctamente.

El usuario es el único responsable de la selección, el uso y la interpretación de los datos que consulta, busca y transmite en internet.

Velox Sàrl se reserva el derecho de modificar este aviso legal en cualquier momento. El usuario deberá respetar la versión disponible en el sitio web en el momento de su uso.`,
    },
    {
      title: '3. Contenido del sitio web',
      body: `Velox Sàrl no será responsable de la presencia de virus o software malicioso en su sitio web.

Velox Sàrl se reserva el derecho de corregir el contenido de este sitio en cualquier momento y sin previo aviso.

Velox Sàrl realiza esfuerzos razonables para ofrecer información y recursos disponibles y verificados, pero no puede ser considerada responsable de errores, indisponibilidad o invalidez de la información.

En ningún caso la información contenida en este sitio constituye una garantía. Para más información, contacte directamente con Velox Sàrl.

Todo usuario que detecte inexactitudes, información errónea o de carácter manifiestamente ilícito puede informar a Velox Sàrl en la dirección indicada en la sección 1.`,
    },
    {
      title: '4. Uso de hipervínculos',
      body: `Este sitio puede incluir información accesible mediante hipervínculos a otros sitios no desarrollados por Velox Sàrl.

Velox Sàrl no será en ningún caso responsable de los hipervínculos a otros sitios desde este sitio web.

La existencia de un enlace desde este sitio hacia otro no constituye aprobación ni garantía alguna por parte de Velox Sàrl respecto al contenido de ese sitio ni a la persona que lo explota.

La creación de hipervínculos hacia este sitio requiere la autorización previa y expresa de Velox Sàrl.`,
    },
    {
      title: '5. Propiedad intelectual',
      body: `Toda la información disponible en este sitio está protegida por derechos de autor. Salvo indicación contraria, se permite la reproducción de los textos con fines no comerciales, incluidos los educativos, con las siguientes condiciones:

(a) difusión gratuita;
(b) respeto de la integridad del contenido reproducido, sin modificación ni alteración alguna; y
(c) mención expresa de www.myvelox.eu como fuente, junto con la indicación de que los derechos de reproducción están reservados y limitados.

Cualquier excepción a estas condiciones requiere la autorización previa por escrito de Velox Sàrl y, en su caso, del autor del contenido.

Salvo indicación contraria, las fotografías de este sitio están protegidas por derechos de autor y son propiedad de Velox Sàrl.`,
    },
    {
      title: '6. Protección de datos',
      body: `Para obtener información sobre cómo este sitio web y el portal de transportistas tratan los datos personales, incluido el uso de cookies, consulte la política de privacidad y la política de cookies de Velox, ambas accesibles mediante un enlace dedicado en el sitio web.`,
    },
    {
      title: '7. Responsabilidad',
      body: `El usuario acepta que todo acceso o uso de este sitio se realiza bajo su propia responsabilidad.

Velox Sàrl declina toda responsabilidad por daños directos o indirectos derivados del uso del sitio, en particular en relación con la interrupción o el cese de su funcionamiento, fallos derivados del mantenimiento o la actualización de los sistemas informáticos de Velox Sàrl, fallos técnicos, sobrecargas de red, fallos en las líneas telefónicas, errores o negligencia de proveedores de acceso a internet o de terceros, o un virus contraído a través de internet.`,
    },
    {
      title: '8. Legislación aplicable y jurisdicción',
      body: `Este aviso legal se rige por la legislación del Gran Ducado de Luxemburgo.

Se informa al usuario de que únicamente los tribunales de Luxemburgo, Gran Ducado de Luxemburgo, son competentes para resolver cualquier litigio relativo al uso, la interpretación o la aplicación de la información y los datos que figuran en este sitio.`,
    },
    {
      title: '9. Contacto',
      body: `Para cualquier consulta relativa a este aviso legal, contacte con Velox Sàrl mediante los datos indicados en la sección 1.`,
    },
  ],
};

const de: LegalNoticeContent = {
  title: 'Impressum',
  subtitle: 'Velox Sàrl — www.myvelox.eu',
  lastUpdated: 'Letzte Aktualisierung: 31.08.2026',
  back: 'Zurück zur Startseite',
  sections: [
    {
      title: '1. Angaben zum Betreiber der Website',
      body: `Diese Website, erreichbar unter www.myvelox.eu, wird betrieben von:

Velox Sàrl, eine Gesellschaft mit beschränkter Haftung (société à responsabilité limitée) nach dem Recht des Großherzogtums Luxemburg.

Sitz: 38, rue Wilson, L-2732 Luxemburg, Großherzogtum Luxemburg.
Handels- und Gesellschaftsregister Luxemburg (RCS Luxembourg): B306257.
Stammkapital: 12.000 EUR (fest).
Niederlassungsgenehmigung Nr. 10187403/0, erteilt nach dem geänderten Gesetz vom 2. September 2011.
Umsatzsteuer-Identifikationsnummer: LU37407003.
E-Mail: info@myvelox.eu.`,
    },
    {
      title: '2. Allgemeine Nutzungsbedingungen',
      body: `Der Nutzer dieser Website (der „Nutzer") bestätigt, überprüft zu haben, dass die verwendete Computerkonfiguration keine Viren oder sonstige Schadsoftware enthält und ordnungsgemäß funktioniert.

Der Nutzer ist allein verantwortlich für die Auswahl, Nutzung und Auslegung der Daten, die er im Internet abruft, sucht und übermittelt.

Velox Sàrl behält sich das Recht vor, dieses Impressum jederzeit zu ändern. Maßgeblich ist die zum Zeitpunkt der Nutzung auf der Website verfügbare Fassung.`,
    },
    {
      title: '3. Inhalte der Website',
      body: `Velox Sàrl haftet nicht für das Vorhandensein von Viren oder Schadsoftware auf ihrer Website.

Velox Sàrl behält sich das Recht vor, die Inhalte dieser Website jederzeit und ohne Vorankündigung zu korrigieren.

Velox Sàrl unternimmt angemessene Anstrengungen, um Nutzern verfügbare und geprüfte Informationen bereitzustellen, haftet jedoch nicht für Fehler, Nichtverfügbarkeit oder die Ungültigkeit der Informationen.

Die auf dieser Website enthaltenen Informationen stellen in keinem Fall eine Garantie dar. Für weitere Informationen wenden Sie sich bitte direkt an Velox Sàrl.

Nutzer, die Ungenauigkeiten, fehlerhafte oder offensichtlich rechtswidrige Informationen feststellen, werden gebeten, Velox Sàrl unter der in Abschnitt 1 genannten Adresse zu informieren.`,
    },
    {
      title: '4. Verwendung von Hyperlinks',
      body: `Diese Website kann Informationen enthalten, die über Hyperlinks zu anderen, nicht von Velox Sàrl entwickelten Websites bereitgestellt werden.

Velox Sàrl haftet in keinem Fall für Hyperlinks von dieser Website zu anderen Websites.

Das Bestehen eines Links von dieser Website zu einer anderen Website stellt weder eine Zustimmung noch eine Garantie seitens Velox Sàrl hinsichtlich des Inhalts oder des Betreibers dieser Website dar.

Das Setzen von Hyperlinks auf diese Website bedarf der ausdrücklichen vorherigen Zustimmung von Velox Sàrl.`,
    },
    {
      title: '5. Geistiges Eigentum',
      body: `Alle auf dieser Website verfügbaren Informationen sind urheberrechtlich geschützt. Sofern nicht anders angegeben, ist die Vervielfältigung von Texten dieser Website zu nicht kommerziellen Zwecken, einschließlich Bildungszwecken, unter folgenden Bedingungen gestattet:

(a) kostenlose Verbreitung;
(b) Wahrung der Integrität des vervielfältigten Inhalts ohne jegliche Änderung; und
(c) ausdrückliche Nennung von www.myvelox.eu als Quelle sowie der Hinweis, dass die Vervielfältigungsrechte vorbehalten und beschränkt sind.

Jede Abweichung von diesen Bedingungen bedarf der vorherigen schriftlichen Zustimmung von Velox Sàrl und gegebenenfalls des Urhebers des Inhalts.

Sofern nicht anders angegeben, sind Fotografien auf dieser Website urheberrechtlich geschützt und Eigentum von Velox Sàrl.`,
    },
    {
      title: '6. Datenschutz',
      body: `Informationen darüber, wie diese Website und das Transporteur-Portal personenbezogene Daten verarbeiten, einschließlich der Verwendung von Cookies, finden Sie in der Datenschutzerklärung und der Cookie-Richtlinie von Velox, die beide über einen eigenen Link auf der Website abrufbar sind.`,
    },
    {
      title: '7. Haftung',
      body: `Der Nutzer akzeptiert, dass jeder Zugriff auf diese Website und deren Nutzung auf eigenes Risiko erfolgt.

Velox Sàrl lehnt jede Haftung für direkte oder indirekte Schäden ab, die durch die Nutzung der Website entstehen können, insbesondere im Zusammenhang mit einer Unterbrechung oder Einstellung des Betriebs der Website, Fehlfunktionen infolge von Wartung oder Aktualisierung der IT-Systeme von Velox Sàrl, technischen Störungen, Netzüberlastungen, Störungen von Telefonleitungen, Fehlern oder Fahrlässigkeit von Internetdienstanbietern oder Dritten oder einem über das Internet erworbenen Virus.`,
    },
    {
      title: '8. Anwendbares Recht und Gerichtsstand',
      body: `Dieses Impressum unterliegt dem Recht des Großherzogtums Luxemburg.

Der Nutzer wird darauf hingewiesen, dass ausschließlich die Gerichte von Luxemburg, Großherzogtum Luxemburg, für Streitigkeiten über die Nutzung, Auslegung oder Umsetzung der auf dieser Website enthaltenen Informationen und Daten zuständig sind.`,
    },
    {
      title: '9. Kontakt',
      body: `Bei Fragen zu diesem Impressum wenden Sie sich bitte an Velox Sàrl unter den in Abschnitt 1 genannten Kontaktdaten.`,
    },
  ],
};

const it: LegalNoticeContent = {
  title: 'Note legali',
  subtitle: 'Velox Sàrl — www.myvelox.eu',
  lastUpdated: 'Ultimo aggiornamento: 31.08.2026',
  back: 'Torna alla home',
  sections: [
    {
      title: '1. Identificazione del gestore del sito',
      body: `Questo sito web, disponibile all'indirizzo www.myvelox.eu, è gestito da:

Velox Sàrl, società a responsabilità limitata (société à responsabilité limitée) di diritto lussemburghese.

Sede legale: 38, rue Wilson, L-2732 Lussemburgo, Granducato di Lussemburgo.
Registro del commercio e delle società del Lussemburgo (RCS Luxembourg): B306257.
Capitale sociale: 12.000 EUR (fisso).
Autorizzazione all'esercizio N° 10187403/0, rilasciata ai sensi della legge modificata del 2 settembre 2011.
Numero di identificazione IVA: LU37407003.
Email: info@myvelox.eu.`,
    },
    {
      title: '2. Condizioni generali di utilizzo',
      body: `L'utente di questo sito (l'«utente») dichiara di aver verificato che la configurazione informatica utilizzata non contiene virus o altro software dannoso e che funziona correttamente.

L'utente è l'unico responsabile della scelta, dell'utilizzo e dell'interpretazione dei dati che consulta, ricerca e trasmette su internet.

Velox Sàrl si riserva il diritto di modificare le presenti note legali in qualsiasi momento. L'utente è tenuto a rispettare la versione disponibile sul sito al momento dell'utilizzo.`,
    },
    {
      title: '3. Contenuti del sito',
      body: `Velox Sàrl non è responsabile della presenza di virus o software dannosi sul proprio sito.

Velox Sàrl si riserva il diritto di correggere i contenuti del sito in qualsiasi momento e senza preavviso.

Velox Sàrl compie sforzi ragionevoli per offrire informazioni e risorse disponibili e verificate, ma non può essere ritenuta responsabile di errori, indisponibilità o non validità delle informazioni.

In nessun caso le informazioni contenute in questo sito costituiscono una garanzia. Per ulteriori informazioni, contattare direttamente Velox Sàrl.

Gli utenti che riscontrino inesattezze, informazioni errate o manifestamente illecite sono invitati a informare Velox Sàrl all'indirizzo indicato nella sezione 1.`,
    },
    {
      title: '4. Utilizzo dei collegamenti ipertestuali',
      body: `Questo sito può contenere informazioni rese disponibili tramite collegamenti ipertestuali ad altri siti non sviluppati da Velox Sàrl.

Velox Sàrl non è in alcun caso responsabile dei collegamenti ipertestuali verso altri siti presenti su questo sito.

L'esistenza di un collegamento da questo sito verso un altro non costituisce approvazione o garanzia da parte di Velox Sàrl in merito al contenuto di tale sito o al soggetto che lo gestisce.

La creazione di collegamenti ipertestuali verso questo sito è soggetta alla preventiva ed esplicita approvazione di Velox Sàrl.`,
    },
    {
      title: '5. Proprietà intellettuale',
      body: `Tutte le informazioni disponibili su questo sito sono protette dal diritto d'autore. Salvo diversa indicazione, la riproduzione dei testi per finalità non commerciali, anche didattiche, è consentita alle seguenti condizioni:

(a) diffusione gratuita;
(b) rispetto dell'integrità del contenuto riprodotto, senza modifiche o alterazioni di alcun tipo; e
(c) menzione esplicita di www.myvelox.eu come fonte, unitamente all'indicazione che i diritti di riproduzione sono riservati e limitati.

Qualsiasi eccezione a tali condizioni è soggetta alla previa approvazione scritta di Velox Sàrl e, se del caso, dell'autore del contenuto.

Salvo diversa indicazione, le fotografie presenti sul sito sono protette dal diritto d'autore e di proprietà di Velox Sàrl.`,
    },
    {
      title: '6. Protezione dei dati',
      body: `Per informazioni sul trattamento dei dati personali da parte di questo sito e del portale trasportatori, incluso l'uso dei cookie, si rinvia all'informativa sulla privacy e alla politica sui cookie di Velox, entrambe accessibili tramite un link dedicato sul sito.`,
    },
    {
      title: '7. Responsabilità',
      body: `L'utente accetta che qualsiasi accesso o utilizzo di questo sito avvenga a proprio rischio.

Velox Sàrl declina ogni responsabilità per danni diretti o indiretti derivanti dall'uso del sito, in particolare in relazione a interruzioni o cessazioni del funzionamento del sito, malfunzionamenti dovuti a manutenzione o aggiornamento dei sistemi informatici di Velox Sàrl, guasti tecnici, sovraccarichi di rete, guasti delle linee telefoniche, errori o negligenze di fornitori di accesso a internet o di terzi, o virus contratti tramite internet.`,
    },
    {
      title: '8. Legge applicabile e foro competente',
      body: `Le presenti note legali sono disciplinate dal diritto del Granducato di Lussemburgo.

L'utente è informato che solo i tribunali di Lussemburgo, Granducato di Lussemburgo, sono competenti a decidere qualsiasi controversia relativa all'uso, all'interpretazione o all'applicazione delle informazioni e dei dati presenti su questo sito.`,
    },
    {
      title: '9. Contatti',
      body: `Per qualsiasi domanda relativa alle presenti note legali, contattare Velox Sàrl utilizzando i recapiti indicati nella sezione 1.`,
    },
  ],
};

const CONTENT: Record<string, LegalNoticeContent> = { en, fr, es, de, it };

export const getLegalNotice = (language: string): LegalNoticeContent =>
  CONTENT[(language || 'fr').split('-')[0]] ?? fr;
